/**
 * Logout helper — wipes the session atomically.
 *
 * Called by the (future T8) user menu. Intentionally split from the React
 * component layer so headless / programmatic flows can reuse it (e.g. a
 * session-expired handler).
 *
 * Order of operations:
 *   1. POST /api/auth/logout — fire-and-forget (legacy parity; server
 *      revokes refresh cookie, but even if this 401s we still clear locally).
 *   2. tokens.clearTokens() — wipe access token + cached user.
 *   3. queryClient.clear() — invalidate every cached query so any orphaned
 *      UI doesn't display the previous user's data while /login mounts.
 *   4. Caller navigates to /login (we don't `window.location = …` here —
 *      that is for the caller so they can use the router).
 */

import type { QueryClient } from '@tanstack/react-query';
import type { ApiBundle } from '@shopista/shared/api/hooks';
import type { WebTokenStorage } from './tokenStorage';

export interface PerformLogoutInput {
  api: ApiBundle;
  tokens: WebTokenStorage;
  queryClient: QueryClient;
}

export async function performLogout({
  api,
  tokens,
  queryClient,
}: PerformLogoutInput): Promise<void> {
  try {
    await api.auth.logout();
  } catch {
    // Ignored — we still want to clear local state even if the server call
    // fails (offline, stale token, whatever).
  }
  tokens.clearTokens();
  queryClient.clear();
}
